export const GLOBAL_SEARCH_TYPES = {
  ALL: "All",
  OBLIGATIONS: "Obligations",
  FILES: "Files",
};

/** Legacy type stored in localStorage before Files rename. */
export const LEGACY_EVIDENCES_TYPE = "Evidences";

export const isFilesSearchType = (type) =>
  type === GLOBAL_SEARCH_TYPES.FILES || type === LEGACY_EVIDENCES_TYPE;

export const normalizeRecentlyViewedItem = (item) => {
  if (!item || typeof item !== "object") return item;
  if (item.type === LEGACY_EVIDENCES_TYPE) {
    return { ...item, type: GLOBAL_SEARCH_TYPES.FILES };
  }
  return item;
};

export const formatSearchDate = (iso) => {
  if (!iso) return "";
  const value = String(iso);
  const ymd = value.slice(0, 10);
  return ymd.includes("-") ? ymd.replaceAll("-", "/") : ymd;
};

export const getSearchModeForSelection = (selectedType) => {
  if (selectedType === GLOBAL_SEARCH_TYPES.ALL) {
    return "all";
  }
  if (
    selectedType === GLOBAL_SEARCH_TYPES.FILES ||
    selectedType === LEGACY_EVIDENCES_TYPE
  ) {
    return "files";
  }
  return "obligations";
};

const isObjectId = (v) => /^[a-fA-F0-9]{24}$/.test(String(v || ""));

/**
 * When every access-scope option is selected, omit the filter (same as "no filter").
 * Avoids POSTing huge $in lists that stall search on large tenants.
 */
export const resolveSearchFilterIds = (selectedKeys, accessScopeItems) => {
  const selected = (selectedKeys || []).filter(isObjectId);
  const scopeIds = (accessScopeItems || [])
    .map((item) => item?.id)
    .filter(isObjectId);

  if (scopeIds.length === 0) {
    return selected.length > 0 ? selected : undefined;
  }

  if (selected.length === 0) return undefined;

  const selectedSet = new Set(selected);
  const allSelected =
    scopeIds.length === selectedSet.size &&
    scopeIds.every((id) => selectedSet.has(id));

  if (allSelected) return undefined;
  return selected;
};

export const buildGlobalSearchPayload = ({
  q,
  selectedType,
  selectedFrameworkKeys,
  accessScopeFrameworks,
  selectedResponsibleKeys,
  accessScopeUsers,
  selectedEntityKeys,
  accessScopeEntities,
  cursor = null,
  limit = 50,
}) => {
  const hasExplicitEmptySelection = [
    [selectedFrameworkKeys, accessScopeFrameworks],
    [selectedResponsibleKeys, accessScopeUsers],
    [selectedEntityKeys, accessScopeEntities],
  ].some(([selectedKeys, scopeItems]) => {
    const scopeIds = (scopeItems || [])
      .map((item) => item?.id)
      .filter(isObjectId);
    const selectedIds = (selectedKeys || []).filter(isObjectId);
    return scopeIds.length > 0 && selectedIds.length === 0;
  });
  const frameworks = resolveSearchFilterIds(
    selectedFrameworkKeys,
    accessScopeFrameworks
  );
  const responsibles = resolveSearchFilterIds(
    selectedResponsibleKeys,
    accessScopeUsers
  );
  const entities = resolveSearchFilterIds(
    selectedEntityKeys,
    accessScopeEntities
  );

  return {
    q,
    search_mode: getSearchModeForSelection(selectedType),
    ...(hasExplicitEmptySelection ? { match_none: true } : {}),
    ...(frameworks ? { frameworks } : {}),
    ...(responsibles ? { responsibles } : {}),
    ...(entities ? { entities } : {}),
    ...(cursor ? { cursor } : {}),
    limit,
  };
};

export const getObligationDrawerTarget = (item) => ({
  id: item?.group_id || item?.id || null,
  recurrenceId: item?.obligation_id || null,
});

/** Build obligation display title from tag + name (FE-owned; API no longer sends title). */
export const formatObligationSearchTitle = (oblTag, oblName) => {
  const tag = String(oblTag || "").trim();
  const name = String(oblName || "").trim();
  if (tag && name) return `${tag} — ${name}`;
  return tag || name || "";
};

const normalizeObligationHit = (hit) => {
  const mainResponsible = hit?.main_responsible;
  const groupId = hit?.group_id || hit?.result_id;
  const recurrenceId = hit?.obligation_id;
  const oblTag = hit?.obl_tag || "";
  const oblName = hit?.obl_name || "";

  return {
    hit_id: hit?.hit_id || null,
    id: hit?.result_id || groupId,
    group_id: groupId,
    obligation_id: recurrenceId,
    type: GLOBAL_SEARCH_TYPES.OBLIGATIONS,
    title: formatObligationSearchTitle(oblTag, oblName) || "Obligation",
    sub: "",
    obl_tag: oblTag,
    obl_name: oblName,
    responsible_name: mainResponsible?.name || "",
    responsible_id: mainResponsible?.id || "",
    status: "",
    due_date: "",
    framework_name: hit?.framework_snapshot?.framework_name || "",
    framework_filter_type: hit?.framework_snapshot?.filter_type || "",
    framework_bg: hit?.framework_snapshot?.background_color || "#959595ff",
    framework_font: hit?.framework_snapshot?.font_color || "",
  };
};

const normalizeEvidenceHit = (hit) => {
  const groupId = hit?.group_id;
  const evidenceId = hit?.evidence_id || hit?.result_id;
  const fileName = hit?.file_name || "";

  return {
    hit_id: hit?.hit_id || null,
    id: hit?.result_id || evidenceId,
    evidence_id: evidenceId,
    obligation_id: hit?.obligation_id,
    group_id: groupId,
    type: GLOBAL_SEARCH_TYPES.FILES,
    title: fileName || "File",
    file_name: fileName,
    uploaded_at: formatSearchDate(hit?.uploaded_at),
    criterion_tag: hit?.criterion_tag || "",
    criterion_name: hit?.criterion_name || "",
    obl_tag: hit?.obl_tag || "",
    obl_name: hit?.obl_name || "",
    framework_name: hit?.framework_snapshot?.framework_name || "",
    framework_filter_type: hit?.framework_snapshot?.filter_type || "",
    framework_bg: hit?.framework_snapshot?.background_color || "#959595ff",
    framework_font: hit?.framework_snapshot?.font_color || "",
  };
};

export const mergeGlobalSearchResults = (currentResults, incomingResults) => {
  const byIdentity = new Map();
  [...(currentResults || []), ...(incomingResults || [])].forEach((item) => {
    const identity = item?.hit_id || `${item?.type || ""}:${item?.id || ""}`;
    if (!byIdentity.has(identity)) byIdentity.set(identity, item);
  });
  return Array.from(byIdentity.values());
};

/** Normalize the unified typed result array without changing server order. */
export const normalizeGlobalSearchResponse = (data) => {
  if (!Array.isArray(data?.results)) return [];
  return data.results
    .filter((result) => ["obligation", "evidence"].includes(result?.type))
    .map((result) =>
      result.type === "evidence"
        ? normalizeEvidenceHit(result)
        : normalizeObligationHit(result)
    );
};

/** Read the opaque continuation cursor for the unified result stream. */
export const getGlobalSearchPageState = (data) => {
  const nextCursor =
    data?.pagination?.has_more === true &&
    typeof data?.pagination?.next_cursor === "string" &&
    data.pagination.next_cursor.length > 0
      ? data.pagination.next_cursor
      : null;
  return {
    hasMore: nextCursor !== null,
    nextCursor,
  };
};
